import { useEffect, useState } from 'react';

import { searchPlaces, type GeocodeResult } from './geocoding';
import type { GeoCentre } from './defaultCentre';
import { Spinner, Text, TextField } from '../ui';

/** How long to wait after the last keystroke before searching. */
const DEBOUNCE_MS = 300;

/** Below this, a query matches half the planet and the results are noise. */
const MIN_QUERY_LENGTH = 3;

/** Props for {@link LocationSearch}. */
export interface LocationSearchProps {
  /** Called with the place the user picked. */
  readonly onSelect: (centre: GeoCentre) => void;
  /** Called when the user dismisses the search without choosing. */
  readonly onCancel: () => void;
}

/**
 * A typeahead for "show me events somewhere else".
 *
 * Debounced so a five-letter city does not cost five requests, and every in-flight request is
 * aborted when the query moves on, so a slow early response cannot overwrite the results for
 * what the user has since typed.
 *
 * @param props - The component props.
 * @returns The search element.
 */
export function LocationSearch({ onSelect, onCancel }: LocationSearchProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly GeocodeResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
    useEffect(() => {
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);

    const timer = setTimeout(() => {
      void searchPlaces(query, controller.signal).then((found) => {
        if (!controller.signal.aborted) {
          setResults(found);
          setIsSearching(false);
        }
      });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return (
    <div className="gm-location-search">
      <TextField
        // The user opened this by clicking "Change"; focusing what they asked for is expected.
        autoFocus
        label="Search for a place"
        value={query}
        placeholder="City, ZIP or address"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onCancel();
          }
        }}
      />

      {isSearching && (
        <div className="gm-location-search__status">
          <Spinner label="Searching" />
        </div>
      )}

      {!isSearching && results.length > 0 && (
        <ul className="gm-location-search__results">
          {results.map((result) => (
            <li key={result.id}>
              <button
                type="button"
                className="gm-location-search__result"
                onClick={() => onSelect(result)}
              >
                <span className="gm-location-search__result-label">{result.label}</span>
                {result.detail !== '' && (
                  <span className="gm-location-search__result-detail">{result.detail}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!isSearching && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && (
        <div className="gm-location-search__status">
          <Text muted>No places matched that.</Text>
        </div>
      )}
    </div>
  );
}
